import React, { useState } from 'react';
import { Info, PanelLeft, PanelRight, PanelTop, Sun, Check, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react';
import { useFormContext } from '../FormContext';
import { FormStep } from '../FormStep';
import { InfoBox } from '../InfoBox';
import { FormButtons } from '../FormButtons';
export const ExtraElementsStep: React.FC = () => {
  const {
    formData,
    handleOptionSelect,
    handleNumberChange,
    goToPreviousStep,
    goToNextStep
  } = useFormContext();
  // Track which accordion section is currently open
  const [activeSection, setActiveSection] = useState<'left' | 'right' | 'front' | null>('left');
  // Track which option is being previewed on hover
  const [previewOption, setPreviewOption] = useState<{
    section: 'left' | 'right' | 'front' | null;
    option: any | null;
  }>({
    section: null,
    option: null
  });
  // Determine the project type for display text
  const projectType = formData.projectTypes.includes('veranda') ? 'veranda' : formData.projectTypes.includes('overkapping') ? 'overkapping' : 'tuinkamer';
  // Get display text based on project type
  const getProjectTypeText = () => {
    switch (projectType) {
      case 'veranda':
        return {
          title: 'Extra elementen voor uw veranda',
          description: 'Kies stap voor stap de zijwanden en voorkant van uw veranda.',
          leftSideTitle: 'Zijwand links',
          rightSideTitle: 'Zijwand rechts',
          frontSideTitle: 'Voorkant'
        };
      case 'overkapping':
        return {
          title: 'Extra elementen voor uw overkapping',
          description: 'Kies stap voor stap de zijwanden en voorkant van uw overkapping.',
          leftSideTitle: 'Zijwand links',
          rightSideTitle: 'Zijwand rechts',
          frontSideTitle: 'Voorkant'
        };
      case 'tuinkamer':
        return {
          title: 'Extra elementen voor uw tuinkamer',
          description: 'Kies stap voor stap de zijwanden en voorkant van uw tuinkamer.',
          leftSideTitle: 'Zijwand links',
          rightSideTitle: 'Zijwand rechts',
          frontSideTitle: 'Voorkant'
        };
      default:
        return {
          title: 'Extra elementen',
          description: 'Kies stap voor stap de zijwanden en voorkant.',
          leftSideTitle: 'Zijwand links',
          rightSideTitle: 'Zijwand rechts',
          frontSideTitle: 'Voorkant'
        };
    }
  };
  const displayText = getProjectTypeText();
  // Define side options for LEFT side
  const leftSideOptions = [{
    id: 'none',
    name: 'Geen zijwand',
    description: 'Open zijkant zonder wand',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gvafo4Cg9aCzmMyUvrvFY2/Geenzijwand.png'
  }, {
    id: 'aluminium-zijwand',
    name: 'Aluminium zijwand',
    description: 'Volledige aluminium wand',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jm39WPWJsRxdYCKtXKBCTN/aluminiuum_zijwand.png'
  }, {
    id: 'glazen-schuifwand',
    name: 'Glazen schuifwanden',
    description: 'Transparante schuifwanden van glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/s16xjFXt2RepTTUpuuuZn3/glazen_schuifwand.png'
  }, {
    id: 'geisoleerde-panelen',
    name: 'Geïsoleerde panelen',
    description: 'Thermisch isolerende wandpanelen',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o8vfjh2ULvKFMwiPePKQtN/geisoleerde_panelen.png'
  }, {
    id: 'aluminium-schuifpui',
    name: 'Aluminium schuifpui',
    description: 'Aluminium schuifpui met glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gDSqPRPtFWpV6NBee6c55m/Links_-_Aluminium_Schuif.png'
  }];
  // Define side options for RIGHT side
  const rightSideOptions = [{
    id: 'none',
    name: 'Geen zijwand',
    description: 'Open zijkant zonder wand',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gvafo4Cg9aCzmMyUvrvFY2/Geenzijwand.png'
  }, {
    id: 'aluminium-zijwand',
    name: 'Aluminium zijwand',
    description: 'Volledige aluminium wand',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jm39WPWJsRxdYCKtXKBCTN/aluminiuum_zijwand.png'
  }, {
    id: 'glazen-schuifwand',
    name: 'Glazen schuifwanden',
    description: 'Transparante schuifwanden van glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/s16xjFXt2RepTTUpuuuZn3/glazen_schuifwand.png'
  }, {
    id: 'geisoleerde-panelen',
    name: 'Geïsoleerde panelen',
    description: 'Thermisch isolerende wandpanelen',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o8vfjh2ULvKFMwiPePKQtN/geisoleerde_panelen.png'
  }, {
    id: 'aluminium-schuifpui',
    name: 'Aluminium schuifpui',
    description: 'Aluminium schuifpui met glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gDSqPRPtFWpV6NBee6c55m/Links_-_Aluminium_Schuif.png'
  }];
  // Front side options
  const frontOptions = [{
    id: 'open',
    name: 'Open',
    description: 'Volledig open voorkant',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pNweyyXmLqzsAciTjLVkJU/Open_voorkant.png'
  }, {
    id: 'glazen-schuifwand',
    name: 'Glazen schuifwand',
    description: 'Transparante schuifwanden van glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/84zpKzg46LczYiTVAqQm5S/Glazen_schuidwanden.png'
  }, {
    id: 'aluminium-schuifpui',
    name: 'Aluminium schuifpui',
    description: 'Aluminium schuifpui met glas',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pF9PJpbFMvYvX9gdrQdddC/Aluminium_Schuifpui.png'
  }];
  // Check if sections are completed
  const isLeftSideCompleted = !!formData.leftSide;
  const isRightSideCompleted = !!formData.rightSide;
  const isFrontSideCompleted = !!formData.frontSide;
  // Check if all sections are completed
  const allSectionsCompleted = isLeftSideCompleted && isRightSideCompleted && isFrontSideCompleted;
  // Find the selected options
  const selectedLeftOption = leftSideOptions.find(option => option.id === formData.leftSide);
  const selectedRightOption = rightSideOptions.find(option => option.id === formData.rightSide);
  const selectedFrontOption = frontOptions.find(option => option.id === formData.frontSide);
  // Handle option selection
  const handleSideOptionSelect = (field: string, value: string) => {
    handleOptionSelect(field, value);
    // No auto-progression to next section
  };
  // Toggle accordion sections
  const toggleSection = (section: 'left' | 'right' | 'front') => {
    setActiveSection(activeSection === section ? null : section);
  };
  // Set preview on hover
  const handleOptionHover = (section: 'left' | 'right' | 'front', option: any) => {
    setPreviewOption({
      section,
      option
    });
  };
  // Clear preview when mouse leaves
  const handleOptionLeave = () => {
    setPreviewOption({
      section: null,
      option: null
    });
  };
  // Get the current options array based on section
  const getOptionsForSection = (section: 'left' | 'right' | 'front') => {
    switch (section) {
      case 'left':
        return leftSideOptions;
      case 'right':
        return rightSideOptions;
      case 'front':
        return frontOptions;
      default:
        return [];
    }
  };
  // Get the selected option for a section
  const getSelectedOptionForSection = (section: 'left' | 'right' | 'front') => {
    switch (section) {
      case 'left':
        return selectedLeftOption;
      case 'right':
        return selectedRightOption;
      case 'front':
        return selectedFrontOption;
      default:
        return null;
    }
  };
  // Get completion status for sections
  const getSectionCompletionStatus = () => {
    return {
      left: isLeftSideCompleted,
      right: isRightSideCompleted,
      front: isFrontSideCompleted
    };
  };
  const completedSections = getSectionCompletionStatus();
  return <FormStep title={displayText.title} description={displayText.description} isActive={true} data-id="element-862">
      {/* Progress bar */}
      <div className="bg-white/10 rounded-lg p-4 mb-6" data-id="element-863">
        <div className="flex justify-between mb-2" data-id="element-864">
          <span className="text-sm text-white/80" data-id="element-865">Voortgang</span>
          <span className="text-sm text-white/80" data-id="element-866">
            {Object.values(completedSections).filter(Boolean).length}/3 voltooid
          </span>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-867">
          <div className="h-full bg-[#CDA735] transition-all duration-500" style={{
          width: `${Object.values(completedSections).filter(Boolean).length / 3 * 100}%`
        }} data-id="element-868"></div>
        </div>
      </div>
      <div className="space-y-4 mb-8" data-id="element-869">
        {/* Section 1: Left Side Accordion */}
        <div className={`border ${activeSection === 'left' ? 'border-[#CDA735]' : 'border-white/20'} rounded-lg overflow-hidden transition-colors duration-300`} data-id="element-870">
          {/* Accordion Header */}
          <div className={`flex items-center justify-between p-4 cursor-pointer 
              ${activeSection === 'left' ? 'bg-[#CDA735]/20' : 'bg-white/10'}`} onClick={() => toggleSection('left')} data-id="element-871">
            <div className="flex items-center" data-id="element-872">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3
                ${completedSections.left ? 'bg-[#CDA735] text-white' : 'bg-white/10 text-white/70'}`} data-id="element-873">
                {completedSections.left ? <Check className="h-4 w-4" data-id="element-874" /> : <PanelLeft className="h-4 w-4" data-id="element-875" />}
              </div>
              <div data-id="element-876">
                <h3 className="font-medium text-white" data-id="element-877">
                  1. {displayText.leftSideTitle}
                </h3>
                {completedSections.left && selectedLeftOption && <p className="text-sm text-white/70" data-id="element-878">
                    Gekozen: {selectedLeftOption.name}
                  </p>}
              </div>
            </div>
            <div data-id="element-879">
              {activeSection === 'left' ? <ChevronUp className="h-5 w-5 text-white" data-id="element-880" /> : <ChevronDown className="h-5 w-5 text-white/70" data-id="element-881" />}
            </div>
          </div>
          {/* Accordion Content */}
          {activeSection === 'left' && <div className="p-4 bg-white/5" data-id="element-882">
              {/* Preview Image - Only shown on desktop and at the top */}
              <div className="hidden md:block mb-6" data-id="element-883">
                {(previewOption.section === 'left' || selectedLeftOption) && <div className="relative bg-black/20 rounded-lg overflow-hidden" data-id="element-884">
                    <img src={previewOption.section === 'left' ? previewOption.option.image : selectedLeftOption?.image} alt={previewOption.section === 'left' ? previewOption.option.name : selectedLeftOption?.name} className="w-full h-64 object-contain p-4 filter drop-shadow-md" style={{
                imageRendering: 'auto',
                objectFit: 'contain'
              }} data-id="element-885" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3" data-id="element-886">
                      <p className="text-white font-medium" data-id="element-887">
                        {previewOption.section === 'left' ? previewOption.option.name : selectedLeftOption?.name}
                      </p>
                      <p className="text-white/80 text-sm" data-id="element-888">
                        {previewOption.section === 'left' ? previewOption.option.description : selectedLeftOption?.description}
                      </p>
                    </div>
                  </div>}
                {!previewOption.section && !selectedLeftOption && <div className="bg-black/20 rounded-lg overflow-hidden flex items-center justify-center h-64" data-id="element-889">
                    <div className="text-center p-6 text-white/60" data-id="element-890">
                      <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-891" />
                      <p data-id="element-892">
                        Beweeg over een optie of maak een keuze om een voorbeeld
                        te zien
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Options */}
              <div data-id="element-893">
                <p className="text-white/80 mb-4" data-id="element-894">
                  Kies een optie voor de linker zijwand van uw {projectType}:
                </p>
                {/* Mobile: Stack options vertically */}
                <div className="grid grid-cols-1 gap-3 md:hidden" data-id="element-895">
                  {leftSideOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('leftSide', option.id)} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.leftSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-896">
                      <div className="flex items-center p-2" data-id="element-897">
                        <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-md mr-3" data-id="element-898">
                          <img src={option.image} alt={option.name} className="w-full h-full object-cover filter drop-shadow-sm" style={{
                      imageRendering: 'auto'
                    }} data-id="element-899" />
                        </div>
                        <div className="flex-1" data-id="element-900">
                          <p className="font-medium text-white" data-id="element-901">
                            {option.name}
                          </p>
                          <p className="text-sm text-white/70 mt-1" data-id="element-902">
                            {option.description}
                          </p>
                        </div>
                        {formData.leftSide === option.id && <div className="ml-2 bg-[#CDA735] rounded-full p-1" data-id="element-903">
                            <Check className="h-4 w-4 text-white" data-id="element-904" />
                          </div>}
                      </div>
                    </div>)}
                </div>
                {/* Desktop: Grid layout */}
                <div className="hidden md:grid md:grid-cols-3 gap-4" data-id="element-905">
                  {leftSideOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('leftSide', option.id)} onMouseEnter={() => handleOptionHover('left', option)} onMouseLeave={handleOptionLeave} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.leftSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10 transform scale-[1.02]' : 'bg-white/5 hover:bg-white/10 hover:transform hover:scale-[1.02]'}`} data-id="element-906">
                      <div className="relative h-36 overflow-hidden" data-id="element-907">
                        <img src={option.image} alt={option.name} className="w-full h-full object-contain p-2 filter drop-shadow-md" style={{
                    imageRendering: 'auto'
                  }} data-id="element-908" />
                        {formData.leftSide === option.id && <div className="absolute top-2 right-2 bg-[#CDA735] rounded-full p-1" data-id="element-909">
                            <Check className="h-3 w-3 text-white" data-id="element-910" />
                          </div>}
                      </div>
                      <div className="p-3" data-id="element-911">
                        <p className="text-sm font-medium text-white" data-id="element-912">
                          {option.name}
                        </p>
                        <p className="text-xs text-white/70 mt-1" data-id="element-913">
                          {option.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="mt-4 flex justify-end" data-id="element-914">
                <button onClick={() => toggleSection('right')} className="bg-[#CDA735] hover:bg-[#B89429] text-white px-4 py-2 rounded-md flex items-center text-sm" disabled={!isLeftSideCompleted} data-id="element-915">
                  <span data-id="element-916">Volgende</span>
                  <ChevronDown className="ml-1 h-4 w-4 rotate-270" data-id="element-917" />
                </button>
              </div>
            </div>}
        </div>
        {/* Section 2: Right Side Accordion */}
        <div className={`border ${activeSection === 'right' ? 'border-[#CDA735]' : 'border-white/20'} rounded-lg overflow-hidden transition-colors duration-300`} data-id="element-918">
          {/* Accordion Header */}
          <div className={`flex items-center justify-between p-4 cursor-pointer 
              ${activeSection === 'right' ? 'bg-[#CDA735]/20' : 'bg-white/10'}`} onClick={() => toggleSection('right')} data-id="element-919">
            <div className="flex items-center" data-id="element-920">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3
                ${completedSections.right ? 'bg-[#CDA735] text-white' : 'bg-white/10 text-white/70'}`} data-id="element-921">
                {completedSections.right ? <Check className="h-4 w-4" data-id="element-922" /> : <PanelRight className="h-4 w-4" data-id="element-923" />}
              </div>
              <div data-id="element-924">
                <h3 className="font-medium text-white" data-id="element-925">
                  2. {displayText.rightSideTitle}
                </h3>
                {completedSections.right && selectedRightOption && <p className="text-sm text-white/70" data-id="element-926">
                    Gekozen: {selectedRightOption.name}
                  </p>}
              </div>
            </div>
            <div data-id="element-927">
              {activeSection === 'right' ? <ChevronUp className="h-5 w-5 text-white" data-id="element-928" /> : <ChevronDown className="h-5 w-5 text-white/70" data-id="element-929" />}
            </div>
          </div>
          {/* Accordion Content */}
          {activeSection === 'right' && <div className="p-4 bg-white/5" data-id="element-930">
              {/* Preview Image - Only shown on desktop and at the top */}
              <div className="hidden md:block mb-6" data-id="element-931">
                {(previewOption.section === 'right' || selectedRightOption) && <div className="relative bg-black/20 rounded-lg overflow-hidden" data-id="element-932">
                    <img src={previewOption.section === 'right' ? previewOption.option.image : selectedRightOption?.image} alt={previewOption.section === 'right' ? previewOption.option.name : selectedRightOption?.name} className={`w-full h-64 object-contain p-4 filter drop-shadow-md
                        ${previewOption.section === 'right' && previewOption.option.id !== 'none' || selectedRightOption && selectedRightOption.id !== 'none' ? 'scale-x-[-1]' : ''}`} style={{
                imageRendering: 'auto',
                objectFit: 'contain'
              }} data-id="element-933" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3" data-id="element-934">
                      <p className="text-white font-medium" data-id="element-935">
                        {previewOption.section === 'right' ? previewOption.option.name : selectedRightOption?.name}
                      </p>
                      <p className="text-white/80 text-sm" data-id="element-936">
                        {previewOption.section === 'right' ? previewOption.option.description : selectedRightOption?.description}
                      </p>
                    </div>
                  </div>}
                {!previewOption.section && !selectedRightOption && <div className="bg-black/20 rounded-lg overflow-hidden flex items-center justify-center h-64" data-id="element-937">
                    <div className="text-center p-6 text-white/60" data-id="element-938">
                      <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-939" />
                      <p data-id="element-940">
                        Beweeg over een optie of maak een keuze om een voorbeeld
                        te zien
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Options */}
              <div data-id="element-941">
                <p className="text-white/80 mb-4" data-id="element-942">
                  Kies een optie voor de rechter zijwand van uw {projectType}:
                </p>
                {/* Mobile: Stack options vertically */}
                <div className="grid grid-cols-1 gap-3 md:hidden" data-id="element-943">
                  {rightSideOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('rightSide', option.id)} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.rightSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-944">
                      <div className="flex items-center p-2" data-id="element-945">
                        <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-md mr-3" data-id="element-946">
                          <img src={option.image} alt={option.name} className={`w-full h-full object-cover filter drop-shadow-sm ${option.id !== 'none' ? 'scale-x-[-1]' : ''}`} style={{
                      imageRendering: 'auto'
                    }} data-id="element-947" />
                        </div>
                        <div className="flex-1" data-id="element-948">
                          <p className="font-medium text-white" data-id="element-949">
                            {option.name}
                          </p>
                          <p className="text-sm text-white/70 mt-1" data-id="element-950">
                            {option.description}
                          </p>
                        </div>
                        {formData.rightSide === option.id && <div className="ml-2 bg-[#CDA735] rounded-full p-1" data-id="element-951">
                            <Check className="h-4 w-4 text-white" data-id="element-952" />
                          </div>}
                      </div>
                    </div>)}
                </div>
                {/* Desktop: Grid layout */}
                <div className="hidden md:grid md:grid-cols-3 gap-4" data-id="element-953">
                  {rightSideOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('rightSide', option.id)} onMouseEnter={() => handleOptionHover('right', option)} onMouseLeave={handleOptionLeave} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.rightSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10 transform scale-[1.02]' : 'bg-white/5 hover:bg-white/10 hover:transform hover:scale-[1.02]'}`} data-id="element-954">
                      <div className="relative h-36 overflow-hidden" data-id="element-955">
                        <img src={option.image} alt={option.name} className={`w-full h-full object-contain p-2 filter drop-shadow-md ${option.id !== 'none' ? 'scale-x-[-1]' : ''}`} style={{
                    imageRendering: 'auto'
                  }} data-id="element-956" />
                        {formData.rightSide === option.id && <div className="absolute top-2 right-2 bg-[#CDA735] rounded-full p-1" data-id="element-957">
                            <Check className="h-3 w-3 text-white" data-id="element-958" />
                          </div>}
                      </div>
                      <div className="p-3" data-id="element-959">
                        <p className="text-sm font-medium text-white" data-id="element-960">
                          {option.name}
                        </p>
                        <p className="text-xs text-white/70 mt-1" data-id="element-961">
                          {option.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="mt-4 flex justify-between" data-id="element-962">
                <button onClick={() => toggleSection('left')} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md flex items-center text-sm" data-id="element-963">
                  <ChevronUp className="mr-1 h-4 w-4 rotate-90" data-id="element-964" />
                  <span data-id="element-965">Vorige</span>
                </button>
                <button onClick={() => toggleSection('front')} className="bg-[#CDA735] hover:bg-[#B89429] text-white px-4 py-2 rounded-md flex items-center text-sm" disabled={!isRightSideCompleted} data-id="element-966">
                  <span data-id="element-967">Volgende</span>
                  <ChevronDown className="ml-1 h-4 w-4 rotate-270" data-id="element-968" />
                </button>
              </div>
            </div>}
        </div>
        {/* Section 3: Front Side Accordion */}
        <div className={`border ${activeSection === 'front' ? 'border-[#CDA735]' : 'border-white/20'} rounded-lg overflow-hidden transition-colors duration-300`} data-id="element-969">
          {/* Accordion Header */}
          <div className={`flex items-center justify-between p-4 cursor-pointer 
              ${activeSection === 'front' ? 'bg-[#CDA735]/20' : 'bg-white/10'}`} onClick={() => toggleSection('front')} data-id="element-970">
            <div className="flex items-center" data-id="element-971">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3
                ${completedSections.front ? 'bg-[#CDA735] text-white' : 'bg-white/10 text-white/70'}`} data-id="element-972">
                {completedSections.front ? <Check className="h-4 w-4" data-id="element-973" /> : <PanelTop className="h-4 w-4" data-id="element-974" />}
              </div>
              <div data-id="element-975">
                <h3 className="font-medium text-white" data-id="element-976">
                  3. {displayText.frontSideTitle}
                </h3>
                {completedSections.front && selectedFrontOption && <p className="text-sm text-white/70" data-id="element-977">
                    Gekozen: {selectedFrontOption.name}
                  </p>}
              </div>
            </div>
            <div data-id="element-978">
              {activeSection === 'front' ? <ChevronUp className="h-5 w-5 text-white" data-id="element-979" /> : <ChevronDown className="h-5 w-5 text-white/70" data-id="element-980" />}
            </div>
          </div>
          {/* Accordion Content */}
          {activeSection === 'front' && <div className="p-4 bg-white/5" data-id="element-981">
              {/* Preview Image - Only shown on desktop and at the top */}
              <div className="hidden md:block mb-6" data-id="element-982">
                {(previewOption.section === 'front' || selectedFrontOption) && <div className="relative bg-black/20 rounded-lg overflow-hidden" data-id="element-983">
                    <img src={previewOption.section === 'front' ? previewOption.option.image : selectedFrontOption?.image} alt={previewOption.section === 'front' ? previewOption.option.name : selectedFrontOption?.name} className="w-full h-64 object-contain p-4 filter drop-shadow-md" style={{
                imageRendering: 'auto',
                objectFit: 'contain'
              }} data-id="element-984" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3" data-id="element-985">
                      <p className="text-white font-medium" data-id="element-986">
                        {previewOption.section === 'front' ? previewOption.option.name : selectedFrontOption?.name}
                      </p>
                      <p className="text-white/80 text-sm" data-id="element-987">
                        {previewOption.section === 'front' ? previewOption.option.description : selectedFrontOption?.description}
                      </p>
                    </div>
                  </div>}
                {!previewOption.section && !selectedFrontOption && <div className="bg-black/20 rounded-lg overflow-hidden flex items-center justify-center h-64" data-id="element-988">
                    <div className="text-center p-6 text-white/60" data-id="element-989">
                      <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-990" />
                      <p data-id="element-991">
                        Beweeg over een optie of maak een keuze om een voorbeeld
                        te zien
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Options */}
              <div data-id="element-992">
                <p className="text-white/80 mb-4" data-id="element-993">
                  Kies een optie voor de voorkant van uw {projectType}:
                </p>
                {/* Mobile: Stack options vertically */}
                <div className="grid grid-cols-1 gap-3 md:hidden" data-id="element-994">
                  {frontOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('frontSide', option.id)} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.frontSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-995">
                      <div className="flex items-center p-2" data-id="element-996">
                        <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-md mr-3" data-id="element-997">
                          <img src={option.image} alt={option.name} className="w-full h-full object-cover filter drop-shadow-sm" style={{
                      imageRendering: 'auto'
                    }} data-id="element-998" />
                        </div>
                        <div className="flex-1" data-id="element-999">
                          <p className="font-medium text-white" data-id="element-1000">
                            {option.name}
                          </p>
                          <p className="text-sm text-white/70 mt-1" data-id="element-1001">
                            {option.description}
                          </p>
                        </div>
                        {formData.frontSide === option.id && <div className="ml-2 bg-[#CDA735] rounded-full p-1" data-id="element-1002">
                            <Check className="h-4 w-4 text-white" data-id="element-1003" />
                          </div>}
                      </div>
                    </div>)}
                </div>
                {/* Desktop: Grid layout */}
                <div className="hidden md:grid md:grid-cols-3 gap-4" data-id="element-1004">
                  {frontOptions.map(option => <div key={option.id} onClick={() => handleSideOptionSelect('frontSide', option.id)} onMouseEnter={() => handleOptionHover('front', option)} onMouseLeave={handleOptionLeave} className={`rounded-lg cursor-pointer transition-all duration-300 overflow-hidden
                        ${formData.frontSide === option.id ? 'ring-2 ring-[#CDA735] bg-[#CDA735]/10 transform scale-[1.02]' : 'bg-white/5 hover:bg-white/10 hover:transform hover:scale-[1.02]'}`} data-id="element-1005">
                      <div className="relative h-36 overflow-hidden" data-id="element-1006">
                        <img src={option.image} alt={option.name} className="w-full h-full object-contain p-2 filter drop-shadow-md" style={{
                    imageRendering: 'auto'
                  }} data-id="element-1007" />
                        {formData.frontSide === option.id && <div className="absolute top-2 right-2 bg-[#CDA735] rounded-full p-1" data-id="element-1008">
                            <Check className="h-3 w-3 text-white" data-id="element-1009" />
                          </div>}
                      </div>
                      <div className="p-3" data-id="element-1010">
                        <p className="text-sm font-medium text-white" data-id="element-1011">
                          {option.name}
                        </p>
                        <p className="text-xs text-white/70 mt-1" data-id="element-1012">
                          {option.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="mt-4 flex justify-between" data-id="element-1013">
                <button onClick={() => toggleSection('right')} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md flex items-center text-sm" data-id="element-1014">
                  <ChevronUp className="mr-1 h-4 w-4 rotate-90" data-id="element-1015" />
                  <span data-id="element-1016">Vorige</span>
                </button>
                <button onClick={() => setActiveSection(null)} className="bg-[#CDA735] hover:bg-[#B89429] text-white px-4 py-2 rounded-md flex items-center text-sm" disabled={!isFrontSideCompleted} data-id="element-1017">
                  <span data-id="element-1018">Afronden</span>
                  <Check className="ml-1 h-4 w-4" data-id="element-1019" />
                </button>
              </div>
            </div>}
        </div>
      </div>
      {/* All sections completed message */}
      {allSectionsCompleted && !activeSection && <div className="bg-[#CDA735]/20 border border-[#CDA735]/30 rounded-lg p-4 mb-6 flex items-center" data-id="element-1020">
          <Check className="h-5 w-5 text-[#CDA735] mr-3" data-id="element-1021" />
          <div data-id="element-1022">
            <p className="text-white font-medium" data-id="element-1023">Alle keuzes gemaakt!</p>
            <p className="text-sm text-white/80" data-id="element-1024">
              U kunt nu verder gaan naar de volgende stap of uw keuzes aanpassen
              door op een sectie te klikken.
            </p>
          </div>
        </div>}
      {/* LED Lighting - always visible at the bottom */}
      <div className="bg-white/10 rounded-lg p-6 mb-8" data-id="element-1025">
        <div className="flex items-center mb-4" data-id="element-1026">
          <Sun className="h-5 w-5 text-[#CDA735] mr-2" data-id="element-1027" />
          <h3 className="text-lg font-medium text-white" data-id="element-1028">LED Verlichting</h3>
        </div>
        <div className="p-4 bg-white/10 rounded-lg mb-4" data-id="element-1029">
          <div className="flex items-center" data-id="element-1030">
            <div className="bg-[#CDA735]/20 text-[#CDA735] p-2 rounded-full mr-3" data-id="element-1031">
              <Check className="h-5 w-5" data-id="element-1032" />
            </div>
            <div data-id="element-1033">
              <p className="text-white font-medium" data-id="element-1034">10 LED spots inbegrepen</p>
              <p className="text-white/70 text-sm" data-id="element-1035">
                Standaard meegeleverd bij elke {projectType}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4" data-id="element-1036">
          <label className="block text-white/90 font-medium mb-2" data-id="element-1037">
            Aantal extra LED spots (optioneel)
          </label>
          <div className="flex items-center" data-id="element-1038">
            <button type="button" onClick={() => handleNumberChange('ledSpots', Math.max(10, formData.ledSpots - 1))} className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-l-md" data-id="element-1039">
              -
            </button>
            <input type="text" inputMode="numeric" min="10" value={formData.ledSpots} onChange={e => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val >= 10) {
              handleNumberChange('ledSpots', val);
            } else if (e.target.value === '') {
              handleNumberChange('ledSpots', 10);
            }
          }} className="w-16 px-3 py-2 bg-white/10 text-white text-center border-x border-white/20" data-id="element-1040" />
            <button type="button" onClick={() => handleNumberChange('ledSpots', formData.ledSpots + 1)} className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-r-md" data-id="element-1041">
              +
            </button>
            <span className="ml-3 text-white/70" data-id="element-1042">
              (10 standaard + {formData.ledSpots - 10} extra)
            </span>
          </div>
        </div>
      </div>
      <InfoBox icon={<Info data-id="element-1044" />} data-id="element-1043">
        Alle elementen zijn later nog aan te passen in overleg met onze
        adviseur.
      </InfoBox>
      <FormButtons onPrevious={goToPreviousStep} onNext={goToNextStep} isNextDisabled={!allSectionsCompleted} data-id="element-1045" />
    </FormStep>;
};